import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../contexts/StoreContext';
import styles from './StoreSelectionPage.module.css';
import layoutStyles from '../styles/layout.module.css';
import { Store } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/layout/Header';
import placeholderImage from '../assets/placeholder.png';

const StoreSelectionPage: React.FC = () => {
  const {
    userStores,
    isLoadingStores,
    errorFetchingStores,
    selectStore,
    fetchUserStores,
  } = useStore();
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();
  
  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';

  React.useEffect(() => {
    if (errorFetchingStores && /jwt expired/i.test(errorFetchingStores)) {
      logout();
      navigate('/login', { replace: true });
    }
  }, [errorFetchingStores, logout, navigate]);

  const handleStoreSelect = (store: Store) => {
    selectStore(store);
    navigate(`/store/${store.id}/overview`);
  };

  const handleCreateStore = () => {
    navigate('/create-store');
  };

  if (isLoadingStores) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <main className={styles.contentArea}>
            <p className={styles.loadingMessage}>Loading your stores...</p>
          </main>
        </div>
      </div>
    );
  }

  if (errorFetchingStores) {
    if (/jwt expired/i.test(errorFetchingStores)) {
      return null;
    }
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <main className={styles.contentArea}>
            <div className={styles.errorContainer}>
              <p className={styles.errorMessage}>Error loading stores: {errorFetchingStores}</p>
              <button onClick={() => fetchUserStores()} className={styles.button}>Try Again</button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <main className={styles.contentArea}>
          <div className={styles.titleBar}>
            <h2 className={styles.title}>{isAdmin ? 'All Stores' : 'Select a Store'}</h2>
            <button onClick={handleCreateStore} className={`${styles.button} ${styles.createButton}`}>
              {isAdmin ? 'Create New Store' : 'Request a New Store'}
            </button>
          </div>
          {userStores.length > 0 ? (
            <div className={styles.cardsGrid}>
              {userStores.map((store) => (
                <div key={store.id} className={styles.storeCard} onClick={() => handleStoreSelect(store)} tabIndex={0} role="button">
                  <img 
                    src={store.image_url || placeholderImage} 
                    alt={store.store_name} 
                    className={styles.cardImage}
                    onError={(e) => {
                      e.currentTarget.src = placeholderImage;
                    }}
                  />
                  <div className={styles.cardContent}>
                    <div className={styles.cardTitleContainer}>
                      <h3 className={styles.cardTitle}>{store.store_name}</h3>
                      <div className={styles.badgesContainer}>
                        {!store.is_active && (
                          <span className={styles.pendingBadge} title="Store request pending approval">
                            ⏳ Pending
                          </span>
                        )}
                        {isAdmin && store.isCreatedByMe && (
                          <span className={styles.createdBadge} title="Store added by you">
                            ✨ Added by me
                          </span>
                        )}
                      </div>
                    </div>
                    <p className={styles.cardDescription}>{store.store_description}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noStoresContainer}>
              <p>No stores found associated with your account.</p>
              <button onClick={handleCreateStore} className={`${styles.button} ${styles.createButton}`}>
                {isAdmin ? 'Create New Store' : 'Request a New Store'}
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default StoreSelectionPage; 